import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, betreuerByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { AuftragPosition, defaultAuftragPosition, moveUpAuftragPosition } from 'api/auftragPosition';
import { EditIcon, DeleteIcon, UpIcon, RepeatOneIcon, RepeatAllIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { formStyles } from 'styles/formStyles';
import { Auftrag } from 'api/auftrag';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { TerminEdit, TerminEditData } from 'components/Termin/TerminEdit';
import { Termin } from 'api/termin';
import { authSelector } from 'store/auth/selector';
import { AuftragPositionEdit } from './AuftragPositionEdit';
import { AuftragPositionDelete } from './AuftragPositionDelete';
import { AuftragPositionenHeader } from './AuftragPositionenHeader';

interface AuftragPositionenProps {
    kunde: Kunde;
    auftrag: Auftrag;
    auftragPositionen: AuftragPosition[];
    wiedervorlagen: Termin[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch: () => void;
}

export const AuftragPositionen: React.FC<AuftragPositionenProps> = ({
    kunde,
    auftrag,
    auftragPositionen,
    wiedervorlagen,
    viewMode,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const auth = useSelector(authSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const [auftragPositionEditData, setAuftragPositionEditData] = React.useState<AuftragPosition | null>(null);
    const [auftragPositionDeleteData, setAuftragPositionDeleteData] = React.useState<AuftragPosition | null>(null);
    const [terminEditData, setTerminEditData] = React.useState<TerminEditData | null>(null);

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <AuftragPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setAuftragPositionEditData({
                                ...defaultAuftragPosition,
                                auNr: auftrag.nr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {/* hide Reclay lines in short view */}
                    {auftragPositionen
                        .filter((auftragPosition) => viewMode === 'long' || auftragPosition.artikelGruppeNr !== 97)
                        .map((auftragPosition) => {
                            const wiedervorlage = (wiedervorlagen || []).find(
                                (wv) =>
                                    wv.kNr === kunde.nr &&
                                    wv.artikelGruppeNr === auftragPosition.artikelGruppeNr &&
                                    wv.artikelNr === auftragPosition.artikelNr,
                            );

                            return (
                                <tr key={auftragPosition.pos} className={classnames(formStyles.positionenRow)}>
                                    <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                        {auftragPosition.pos}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelGruppeCell,
                                        )}
                                    >
                                        {(artikelGruppenByNr[auftragPosition.artikelGruppeNr] &&
                                            artikelGruppenByNr[auftragPosition.artikelGruppeNr]?.text) ||
                                            auftragPosition.artikelGruppeNr}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenArtikelNrCell,
                                        )}
                                    >
                                        <span>
                                            {auftragPosition.artikelGruppeNr && auftragPosition.artikelNr ? (
                                                <OpenLink
                                                    link={createRecordUrl(
                                                        'lager/artikel',
                                                        undefined,
                                                        getArtikelDetailId(
                                                            auftragPosition.artikelGruppeNr,
                                                            auftragPosition.artikelNr,
                                                        ),
                                                        undefined,
                                                        false,
                                                    )}
                                                    label={auftragPosition.artikelNr}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}
                                    >
                                        {auftragPosition.posText}
                                        {auftragPosition.posBem ? (
                                            <i>
                                                <br />
                                                {auftragPosition.posBem}
                                            </i>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}
                                    >
                                        {auftragPosition.posEinh
                                            ? einheitenByCode[auftragPosition.posEinh].text || auftragPosition.posEinh
                                            : '?'}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMengeCell,
                                        )}
                                    >
                                        {formatDecimal(auftragPosition.posMenge, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatDecimal(auftragPosition.offen, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatMoney(auftragPosition.posEkPreis, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                    >
                                        {formatMoney(auftragPosition.posEPreis, 3)}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenMoneyCell,
                                        )}
                                        title={'VK-Brutto: ' + formatMoney(auftragPosition.posBrutto)}
                                    >
                                        {formatMoney(auftragPosition.posNetto)}
                                    </td>
                                    {/*
                                <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                    {formatMoney(auftragPosition.posBrutto)}
                                </td>
                                */}
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                        title={'VK-Brutto: ' + formatMoney(auftragPosition.posBrutto)}
                                    >
                                        {formatPercent(auftragPosition.posMwst)}
                                    </td>
                                    <td
                                        className={classnames(formStyles.positionenCell, formStyles.positionenLArtCell)}
                                    >
                                        {auftragPosition.lArt}
                                    </td>

                                    {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                                        <td
                                            className={classnames(
                                                formStyles.positionenCell,
                                                formStyles.positionenActionCell,
                                            )}
                                        >
                                            {auftragPosition.artikelGruppeNr && auftragPosition.artikelNr ? (
                                                wiedervorlage ? (
                                                    <RepeatOneIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: wiedervorlage.kNr,
                                                                zfKaNr: wiedervorlage.zfKaNr || 0,
                                                            });
                                                        }}
                                                    />
                                                ) : (
                                                    <RepeatAllIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={() => {
                                                            setTerminEditData({
                                                                kNr: kunde.nr,
                                                                zfKaNr: -1,
                                                                initUserKz: auftrag.telBetreuer
                                                                    ? betreuerByNr[auftrag.telBetreuer]?.kz || auth.kz
                                                                    : auth.kz,
                                                                initKaNr: auftrag.kaNr,
                                                                initKaName: auftrag.kaName,
                                                                initTerminArt: 'W',
                                                                initTerminBetreff:
                                                                    (artikelGruppenByNr[auftragPosition.artikelGruppeNr]
                                                                        ?.text || '?') +
                                                                    ' | ' +
                                                                    auftragPosition.artikelNr,
                                                                initArtikelGruppeNr: auftragPosition.artikelGruppeNr,
                                                                initArtikelNr: auftragPosition.artikelNr,
                                                                initPosText: auftragPosition.posText,
                                                                initPosEinh: auftragPosition.posEinh,
                                                                initPosMenge: auftragPosition.posMenge,
                                                            });
                                                        }}
                                                    />
                                                )
                                            ) : null}
                                        </td>
                                    )}

                                    {allowEdit && (
                                        <>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                {auftragPosition.pos > 1 && (
                                                    <UpIcon
                                                        className={formStyles.positionenAction}
                                                        onClick={async () => {
                                                            await moveUpAuftragPosition(
                                                                auftragPosition.auNr,
                                                                auftragPosition.pos,
                                                            );
                                                            if (refetch) {
                                                                refetch();
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <EditIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setAuftragPositionEditData(auftragPosition)}
                                                />
                                            </td>
                                            <td
                                                className={classnames(
                                                    formStyles.positionenCell,
                                                    formStyles.positionenActionCell,
                                                )}
                                            >
                                                <DeleteIcon
                                                    className={formStyles.positionenAction}
                                                    onClick={() => setAuftragPositionDeleteData(auftragPosition)}
                                                />
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
            {auftragPositionEditData !== null && (
                <AuftragPositionEdit
                    auftrag={auftrag}
                    kunde={kunde}
                    auNr={auftragPositionEditData.auNr}
                    pos={auftragPositionEditData.pos}
                    onClose={(_refetch) => {
                        setAuftragPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {auftragPositionDeleteData !== null && (
                <AuftragPositionDelete
                    auftragPosition={auftragPositionDeleteData}
                    onClose={(_refetch) => {
                        setAuftragPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {terminEditData !== null && (
                <TerminEdit
                    defaultKNr={terminEditData.kNr}
                    zfKaNr={terminEditData.zfKaNr}
                    initUserKz={terminEditData.initUserKz}
                    initKaNr={terminEditData.initKaNr}
                    initTerminArt={terminEditData.initTerminArt}
                    initTerminBetreff={terminEditData.initTerminBetreff}
                    initArtikelGruppeNr={terminEditData.initArtikelGruppeNr}
                    initArtikelNr={terminEditData.initArtikelNr}
                    initPosText={terminEditData.initPosText}
                    initPosEinh={terminEditData.initPosEinh}
                    initPosMenge={terminEditData.initPosMenge}
                    onClose={(doRefetch) => {
                        setTerminEditData(null);
                        if (doRefetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
